import type { PopoverPublicAPI } from '@shopify/polaris';

import { BlockStack, Box, Icon, Popover, Text } from '@shopify/polaris';
import { useEffect, useRef, useState } from 'react';
import { CheckIcon } from '@shopify/polaris-icons';

interface SuggestionsListProps {
  open: boolean;
  width?: string;
  activator: React.ReactElement;
  onClose: () => void;
  suggestions: string[];
  suggestionValue: string;
  onSelect: (suggestion: string) => void;
}

export const SuggestionsList = ({
  open,
  width,
  activator,
  onClose,
  suggestions,
  suggestionValue,
  onSelect,
}: SuggestionsListProps) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const popoverRef = useRef<PopoverPublicAPI>(null);

  useEffect(() => {
    if (popoverRef.current) {
      popoverRef.current.forceUpdatePosition();
    }
  }, [open]);

  return (
    <Popover
      ref={popoverRef}
      active={open}
      fluidContent
      preferredAlignment="right"
      activator={activator}
      onClose={onClose}
      autofocusTarget="none"
    >
      <Box padding="200" width={width}>
        <BlockStack gap="100">
          {suggestions.map((suggestion, index) => {
            const isSelected = suggestion === suggestionValue;

            return (
              <Box
                key={suggestion}
                paddingBlock="100"
                paddingInline="150"
                borderRadius="200"
                background={hoveredIndex === index ? 'bg-surface-hover' : 'bg-surface'}
              >
                <div
                  data-suggestion-id={suggestion}
                  className="cursor-pointer flex items-center justify-between"
                  tabIndex={0}
                  role="button"
                  onClick={() => {
                    onSelect(suggestion);
                    onClose();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      onSelect(suggestion);
                      onClose();
                    }
                  }}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <Text as="p" variant="bodyMd" truncate>
                    {suggestion}
                  </Text>

                  {isSelected && (
                    <span>
                      <Icon source={CheckIcon} />
                    </span>
                  )}
                </div>
              </Box>
            );
          })}
        </BlockStack>
      </Box>
    </Popover>
  );
};

SuggestionsList.displayName = 'SuggestionsList';
